import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Plus, X, Pencil, Trash2, KeyRound, Mail } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const empty = { email: "", name: "", role: "Roaster", password: "" };

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);     // user being edited
  const [pwTarget, setPwTarget] = useState(null);   // user whose password we're setting
  const [form, setForm] = useState(empty);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const [u, r] = await Promise.all([
        api.get("/users"),
        api.get("/roles").catch(() => ({ data: [] })),
      ]);
      setUsers(u.data); setRoles(r.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const ROLES = roles.map((r) => r.name);

  const submitCreate = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/users", form);
      setCreating(false); setForm(empty);
      await load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.patch(`/users/${editing.id}`, {
        name: form.name, email: form.email, role: form.role,
      });
      setEditing(null); setForm(empty);
      await load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post(`/users/${pwTarget.id}/password`, {
        new_password: form.password,
      });
      setPwTarget(null); setForm(empty);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
  };

  const setActive = async (id, is_active) => {
    await api.patch(`/users/${id}`, { is_active });
    await load();
  };

  const setRole = async (id, role) => {
    await api.patch(`/users/${id}`, { role });
    await load();
  };

  const remove = async (u) => {
    if (!window.confirm(`Permanently delete ${u.name} (${u.email})?\n\nThis cannot be undone.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      await load();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    }
  };

  const sendWelcome = async (u) => {
    if (!window.confirm(`Send a fresh welcome email to ${u.name} (${u.email})?\n\nThe email contains a magic link (valid 24 h) to set their password.`)) return;
    try {
      await api.post(`/account/send-welcome/${u.id}`);
      alert(`Welcome email sent to ${u.email}.`);
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    }
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({ email: u.email, name: u.name, role: u.role, password: "" });
    setError("");
  };

  const openPassword = (u) => {
    setPwTarget(u);
    setForm({ ...empty, password: "" });
    setError("");
  };

  return (
    <div data-testid="users-page">
      <PageHeader
        eyebrow="Admin"
        title="Users"
        description="Owner-only. Add staff, edit details, reset passwords or delete accounts."
        actions={(
          <button
            data-testid="users-add-button"
            onClick={() => { setForm(empty); setError(""); setCreating(true); }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)]"
          >
            <Plus className="w-4 h-4" /> Invite user
          </button>
        )}
      />

      <div className="bg-card border border-border rounded overflow-x-auto">
        <table className="w-full text-sm" data-testid="users-table">
          <thead>
            <tr className="bg-secondary">
              <th className="label-tiny px-4 py-3 text-left">Name</th>
              <th className="label-tiny px-4 py-3 text-left">Email</th>
              <th className="label-tiny px-4 py-3 text-left">Role</th>
              <th className="label-tiny px-4 py-3 text-left">Active</th>
              <th className="label-tiny px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Loading…</td></tr>}
            {users.map((u) => {
              const isSelf = me?.id === u.id || me?.email === u.email;
              return (
                <tr key={u.id} className="border-t border-border hover:bg-secondary/40">
                  <td className="px-4 py-2.5">
                    {u.name}
                    {isSelf && <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary/10 text-primary">You</span>}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs">{u.email}</td>
                  <td className="px-4 py-2.5">
                    <select
                      value={u.role}
                      onChange={(e) => setRole(u.id, e.target.value)}
                      data-testid={`users-role-${u.id}`}
                      className="px-2 py-1 border border-border rounded text-sm bg-white"
                    >
                      {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-2.5">
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={u.is_active}
                        onChange={(e) => setActive(u.id, e.target.checked)}
                        data-testid={`users-active-${u.id}`}
                      />
                      <span>{u.is_active ? "Yes" : "No"}</span>
                    </label>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="inline-flex items-center gap-1">
                      <IconButton
                        title="Send welcome email"
                        onClick={() => sendWelcome(u)}
                        data-testid={`users-welcome-${u.id}`}
                      ><Mail className="w-4 h-4" /></IconButton>
                      <IconButton
                        title="Edit user"
                        onClick={() => openEdit(u)}
                        data-testid={`users-edit-${u.id}`}
                      ><Pencil className="w-4 h-4" /></IconButton>
                      <IconButton
                        title="Set password"
                        onClick={() => openPassword(u)}
                        data-testid={`users-password-${u.id}`}
                      ><KeyRound className="w-4 h-4" /></IconButton>
                      <IconButton
                        title={isSelf ? "You cannot delete your own account" : "Delete user"}
                        onClick={() => remove(u)}
                        disabled={isSelf}
                        danger
                        data-testid={`users-delete-${u.id}`}
                      ><Trash2 className="w-4 h-4" /></IconButton>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Create modal */}
      {creating && (
        <Modal title="Invite user" onClose={() => setCreating(false)} testid="users-modal">
          <form onSubmit={submitCreate} className="p-5 space-y-4">
            <Input label="Name *" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="users-input-name" />
            <Input label="Email *" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="users-input-email" />
            <Input label="Initial password * (min 8 chars)" type="password" required minLength={8} value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="users-input-password" />
            <RoleSelect roles={ROLES} value={form.role} onChange={(v) => setForm({ ...form, role: v })} testid="users-input-role" />
            {error && <p className="text-destructive text-sm">{error}</p>}
            <ModalActions onCancel={() => setCreating(false)} submitTestid="users-modal-submit" submitLabel="Create" />
          </form>
        </Modal>
      )}

      {/* Edit modal */}
      {editing && (
        <Modal title={`Edit user — ${editing.name}`} onClose={() => setEditing(null)} testid="users-edit-modal">
          <form onSubmit={submitEdit} className="p-5 space-y-4">
            <Input label="Name *" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="users-edit-name" />
            <Input label="Email *" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="users-edit-email" />
            <RoleSelect roles={ROLES} value={form.role} onChange={(v) => setForm({ ...form, role: v })} testid="users-edit-role" />
            {error && <p className="text-destructive text-sm">{error}</p>}
            <ModalActions onCancel={() => setEditing(null)} submitTestid="users-edit-submit" submitLabel="Save changes" />
          </form>
        </Modal>
      )}

      {/* Password modal */}
      {pwTarget && (
        <Modal title={`Set password — ${pwTarget.name}`} onClose={() => setPwTarget(null)} testid="users-password-modal">
          <form onSubmit={submitPassword} className="p-5 space-y-4">
            <p className="text-xs text-muted-foreground bg-secondary/40 border border-border rounded p-3">
              The user will need to log in with the new password. Existing sessions remain active until they expire.
            </p>
            <Input label="New password * (min 8 chars)" type="password" required minLength={8} value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="users-password-input" autoComplete="new-password" />
            {error && <p className="text-destructive text-sm">{error}</p>}
            <ModalActions onCancel={() => setPwTarget(null)} submitTestid="users-password-submit" submitLabel="Set password" />
          </form>
        </Modal>
      )}
    </div>
  );
}

function IconButton({ children, danger, ...props }) {
  return (
    <button
      {...props}
      className={`p-1.5 rounded transition ${
        props.disabled
          ? "text-muted-foreground/40 cursor-not-allowed"
          : danger
          ? "text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
          : "text-muted-foreground hover:bg-secondary hover:text-foreground"
      }`}
    >
      {children}
    </button>
  );
}

function Modal({ title, onClose, testid, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4" data-testid={testid}>
      <div className="bg-card border border-border rounded shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="font-display text-lg font-bold">{title}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ModalActions({ onCancel, submitTestid, submitLabel }) {
  return (
    <div className="flex justify-end gap-2 pt-2">
      <button type="button" onClick={onCancel} className="px-4 py-2 border border-border rounded text-sm hover:bg-secondary">Cancel</button>
      <button type="submit" data-testid={submitTestid} className="px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)]">{submitLabel}</button>
    </div>
  );
}

function Input({ label, value, onChange, testid, ...rest }) {
  return (
    <div>
      <label className="label-tiny mb-1.5 block">{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full px-3 py-2 bg-white border border-border rounded text-sm focus:outline-none focus:border-primary"
        {...rest}
      />
    </div>
  );
}

function RoleSelect({ roles, value, onChange, testid }) {
  return (
    <div>
      <label className="label-tiny mb-1.5 block">Role *</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full px-3 py-2 bg-white border border-border rounded text-sm focus:outline-none focus:border-primary"
      >
        {roles.map((r) => <option key={r} value={r}>{r}</option>)}
      </select>
    </div>
  );
}
